/**
 * DAFNE Clinical Thresholds
 *
 * All blood glucose, ketone, and clinical thresholds used in the application.
 * These values are based on OZ DAFNE guidelines and should be adjusted
 * if guidelines are updated.
 *
 * Last reviewed: February 2026
 */

// =============================================================================
// TARGET PROFILE TYPES
// =============================================================================

export type TargetProfileType = "standard" | "upper";

export interface PeriodTargetRange {
  low: number;
  high: number;
}

export interface TargetProfile {
  id: TargetProfileType;
  name: string;
  description: string;
  /** Pre-meal targets by period */
  preMeal: {
    B: PeriodTargetRange; // Before Breakfast (fasting)
    L: PeriodTargetRange; // Before Lunch
    EM: PeriodTargetRange; // Before Evening Meal
    BT: PeriodTargetRange; // Before Bed
    "3AM": PeriodTargetRange; // Overnight check
  };
  /** Post-meal target (2 hours after eating) */
  postMeal: PeriodTargetRange;
}

// =============================================================================
// DAFNE STANDARD TARGET PROFILE
// For most people with Type 1 diabetes
// =============================================================================

export const STANDARD_TARGETS: TargetProfile = {
  id: "standard",
  name: "Standard Targets",
  description: "For most people with Type 1 diabetes with good hypo awareness",
  preMeal: {
    B: { low: 4.0, high: 7.0 }, // Fasting / before breakfast
    L: { low: 4.0, high: 7.0 }, // Before lunch
    EM: { low: 4.0, high: 7.0 }, // Before evening meal
    BT: { low: 6.0, high: 8.0 }, // Before bed - slightly higher to avoid overnight hypos
    "3AM": { low: 5.0, high: 8.0 }, // Overnight - avoid nocturnal hypos
  },
  postMeal: { low: 4.0, high: 9.0 }, // 2 hours post-meal
};

// =============================================================================
// DAFNE UPPER TARGET PROFILE
// For those with reduced hypo awareness, elderly, or other risk factors
// =============================================================================

export const UPPER_TARGETS: TargetProfile = {
  id: "upper",
  name: "Upper Targets",
  description:
    "For reduced hypo awareness, elderly, or those at higher risk of hypoglycemia",
  preMeal: {
    B: { low: 6.0, high: 10.0 }, // Fasting / before breakfast
    L: { low: 6.0, high: 10.0 }, // Before lunch
    EM: { low: 6.0, high: 10.0 }, // Before evening meal
    BT: { low: 8.0, high: 10.0 }, // Before bed
    "3AM": { low: 7.0, high: 10.0 }, // Overnight
  },
  postMeal: { low: 6.0, high: 12.0 }, // 2 hours post-meal
};

// =============================================================================
// TARGET PROFILES MAP
// =============================================================================

export const TARGET_PROFILES: Record<TargetProfileType, TargetProfile> = {
  standard: STANDARD_TARGETS,
  upper: UPPER_TARGETS,
};

// =============================================================================
// BLOOD GLUCOSE THRESHOLDS (mmol/L)
// Based on OZDAFNE guidelines
// =============================================================================

export const BG_THRESHOLDS = {
  /** Below this is hypoglycemia - treat immediately (OZDAFNE: < 4.0) */
  HYPO: 4.0,

  /** Default target range lower bound */
  TARGET_LOW_DEFAULT: 4.0,

  /** Default target range upper bound */
  TARGET_HIGH_DEFAULT: 7.0,

  /** Above target but below this is amber zone */
  AMBER_HIGH: 10.0,

  /** Above this is considered high - correction may be needed */
  HIGH: 10.0,

  /** Significantly elevated - extra ketone monitoring advised */
  VERY_HIGH: 14.0,

  /** OZDAFNE: Check ketones when BG ≥ 15.0, even if feeling well */
  KETONE_CHECK_TRIGGER: 15.0,

  /** Severe hyperglycemia - urgent attention needed */
  SEVERE_HIGH: 17.0,
} as const;

// =============================================================================
// KETONE THRESHOLDS (mmol/L)
// =============================================================================

export const KETONE_THRESHOLDS = {
  /** Normal - no action needed */
  NORMAL_MAX: 0.6,

  /** Elevated - monitor and hydrate */
  ELEVATED_MAX: 1.5,

  /** High - DKA risk, needs attention */
  HIGH_MAX: 3.0,

  /** Danger zone - medical emergency at or above this */
  DANGER: 3.0,

  /** At this level, suggest seeking medical help */
  SEEK_HELP: 2.5,
} as const;

// =============================================================================
// KETONE + BG COMBINED THRESHOLDS
// =============================================================================

export const KETONE_BG_COMBINED = {
  /** BG level above which elevated ketones become concerning */
  ELEVATED_KETONE_HIGH_BG: 14.0,

  /** Extra correction percentage for elevated ketones + high BG */
  ELEVATED_EXTRA_CORRECTION: 10,

  /** Extra correction percentage for high/danger ketones */
  HIGH_EXTRA_CORRECTION: 20,
} as const;

// =============================================================================
// KETONE RECHECK INTERVALS (minutes)
// =============================================================================

export const KETONE_RECHECK_MINUTES = {
  /** Recheck interval for elevated ketones */
  ELEVATED: 180, // 3 hours

  /** Recheck interval for high ketones */
  HIGH: 120, // 2 hours

  /** Recheck interval for danger level ketones */
  DANGER: 60, // 1 hour
} as const;

// =============================================================================
// TIME IN RANGE TARGETS (percentage)
// =============================================================================

export const TIR_TARGETS = {
  /** Ideal TIR percentage (3.9-10.0 mmol/L) */
  TARGET: 70,

  /** Minimum acceptable TIR */
  MINIMUM: 50,

  /** Time below range - keep under this */
  BELOW_RANGE_MAX: 4,

  /** Time significantly below range - keep under this */
  VERY_LOW_MAX: 1,
} as const;
